import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  RateHawkSearchParams,
  RateHawkSearchResult,
  RateHawkRoomGroup,
  RateHawkRate,
  RateHawkSearchMode,
  RateHawkSearchFacets,
} from "./types";
import { groupRatesByRoom } from "./ratehawk-accommodation/helpers";
import { RATEHAWK_SEARCH_MOCK } from "@/fixtures/ratehawk-search-mock";
import { RATEHAWK_RATES_BY_HID } from "@/fixtures/ratehawk-rates-mock";

/** Set to true to bypass edge functions and use local fixture data. */
const USE_MOCK = import.meta.env.DEV && import.meta.env.VITE_RATEHAWK_MOCK === "true";

interface UseRateHawkStaysReturn {
  // Search step
  /** Solo la pagina corrente: filtro, ordinamento e taglio li fa la edge function. */
  searchResults: RateHawkSearchResult[];
  /** Strutture proponibili per questa ricerca, prima dei filtri. */
  totalHotels: number | null;
  /** Strutture che superano i filtri correnti — è il numero da mostrare al TD. */
  totalFiltered: number | null;
  /** Pagina corrente (1-based) come confermata dal server. */
  page: number;
  totalPages: number;
  /** Valori selezionabili nei filtri, calcolati sull'intero set di risultati. */
  facets: RateHawkSearchFacets;
  /**
   * Ramo usato dal server per l'ultima ricerca ("region" o "geo").
   * null finche` non si e` cercato, o se la edge function non lo riporta
   * (deploy precedente all'introduzione del ramo region).
   */
  searchMode: RateHawkSearchMode | null;
  isSearching: boolean;
  searchError: string | null;
  searchStays: (params: RateHawkSearchParams) => Promise<void>;
  // Hotelpage step (rates with book_hash)
  roomGroups: RateHawkRoomGroup[];
  selectedHotel: RateHawkSearchResult | null;
  isLoadingRates: boolean;
  ratesError: string | null;
  /**
   * Fetch Hotelpage rates for a specific hotel.
   * Requires the original search params (checkin/checkout/guests/currency/residency)
   * because RateHawk Hotelpage needs them to return accurate availability.
   */
  fetchRates: (
    hotel: RateHawkSearchResult,
    searchParams: RateHawkSearchParams,
  ) => Promise<void>;
  // State management
  clearSearch: () => void;
  clearRates: () => void;
  /** True when running with VITE_RATEHAWK_MOCK=true (no edge function calls). */
  isMockMode: boolean;
}

export function useRateHawkStays(): UseRateHawkStaysReturn {
  const [searchResults, setSearchResults] = useState<RateHawkSearchResult[]>([]);
  const [totalHotels, setTotalHotels] = useState<number | null>(null);
  const [totalFiltered, setTotalFiltered] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [facets, setFacets] = useState<RateHawkSearchFacets>({ kinds: [], amenities: [] });

  /**
   * Chiave della ricerca in cache, restituita dal server. La rimandiamo a ogni
   * cambio di filtro come fast-path; il server la ignora se non corrisponde ai
   * parametri di ricerca, quindi non può servirci risultati di un'altra ricerca.
   */
  const cacheKeyRef = useRef<string | null>(null);

  /**
   * Le richieste di filtro partono ravvicinate (debounce corto, toggle in
   * sequenza) e la rete non garantisce l'ordine di arrivo: senza questo
   * contatore la risposta di un filtro vecchio, se arriva per ultima,
   * sovrascrive quella del filtro corrente. Solo la richiesta più recente ha
   * diritto di scrivere lo stato.
   */
  const searchSeqRef = useRef(0);
  const [searchMode, setSearchMode] = useState<RateHawkSearchMode | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [roomGroups, setRoomGroups] = useState<RateHawkRoomGroup[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<RateHawkSearchResult | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);

  /**
   * Geo search for hotels.
   * In mock mode: returns RATEHAWK_SEARCH_MOCK after a 600ms delay.
   * In production: calls stays-search edge function which merges
   *   SERP (/search/serp/geo/) + Content API (/hotel_content_by_ids).
   */
  const searchStays = useCallback(async (params: RateHawkSearchParams) => {
    const seq = ++searchSeqRef.current;
    const isCurrent = () => searchSeqRef.current === seq;

    setIsSearching(true);
    setSearchError(null);
    // I risultati NON si azzerano qui: un cambio di filtro sostituisce una
    // lista con un'altra, e svuotarla nel mezzo farebbe lampeggiare la UI a
    // ogni casella spuntata. Il vuoto lo decide la risposta.

    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 600));
      if (!isCurrent()) return;
      setSearchResults(RATEHAWK_SEARCH_MOCK);
      setTotalHotels(RATEHAWK_SEARCH_MOCK.length);
      setTotalFiltered(RATEHAWK_SEARCH_MOCK.length);
      setPage(1);
      setTotalPages(1);
      setSearchMode("geo");
      setIsSearching(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("stays-search-rh", {
        body: {
          checkin: params.checkin,
          checkout: params.checkout,
          guests: params.guests,
          latitude: params.latitude,
          longitude: params.longitude,
          radius: params.radius,
          // Modalita` dichiarata dal client e region_id: campi additivi, omessi
          // quando assenti, cosi` il payload verso una edge function precedente
          // resta identico a quello di oggi e continua a funzionare.
          ...(params.search_mode !== undefined ? { search_mode: params.search_mode } : {}),
          ...(params.region_id !== undefined ? { region_id: params.region_id } : {}),
          currency: params.currency ?? "EUR",
          residency: params.residency ?? "it",
          filter: params.filter ?? {},
          sort: params.sort ?? { field: "price", dir: "asc" },
          page: params.page ?? 1,
          page_size: params.page_size ?? 25,
          // Fast-path sulla ricerca gia` in cache. Va mandata anche quando i
          // parametri di ricerca sono cambiati: il server confronta la chiave
          // con quella che ricostruisce e scarta la nostra se non coincide.
          ...(cacheKeyRef.current ? { cache_key: cacheKeyRef.current } : {}),
        },
      });

      if (error) throw error;

      // Risposta di una richiesta ormai superata da un filtro piu` recente:
      // scartarla e` l'unico modo di non tornare indietro nel tempo.
      if (!isCurrent()) return;

      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      // { hotels, total_hotels, total_filtered, page, page_size, total_pages,
      //   facets, cache_key, cached, search_mode }
      const hotels: RateHawkSearchResult[] = parsed?.hotels ?? parsed?.data?.hotels ?? [];
      setSearchResults(hotels);
      setTotalHotels(parsed?.total_hotels ?? hotels.length);
      setTotalFiltered(parsed?.total_filtered ?? hotels.length);
      // La pagina la conferma il server: se i nuovi filtri hanno accorciato la
      // lista, quella richiesta puo` non esistere piu` ed essere stata
      // riportata nell'intervallo valido.
      setPage(parsed?.page ?? 1);
      setTotalPages(parsed?.total_pages ?? 1);
      setFacets(parsed?.facets ?? { kinds: [], amenities: [] });
      cacheKeyRef.current = parsed?.cache_key ?? null;
      setSearchMode((parsed?.search_mode as RateHawkSearchMode | undefined) ?? null);
    } catch (err: unknown) {
      if (!isCurrent()) return;
      const message = err instanceof Error ? err.message : "Search failed";
      setSearchError(message);
    } finally {
      if (isCurrent()) setIsSearching(false);
    }
  }, []);

  /**
   * Fetch Hotelpage rates (with book_hash) for a specific hotel.
   * In mock mode: returns RATEHAWK_RATES_BY_HID[hotel.hid] after a 400ms delay.
   * In production: calls stays-rates-rh edge function (RateHawk /search/hp/).
   * Rates are grouped by room_name into RateHawkRoomGroup[].
   */
  const fetchRates = useCallback(
    async (hotel: RateHawkSearchResult, searchParams: RateHawkSearchParams) => {
      setIsLoadingRates(true);
      setRatesError(null);
      setRoomGroups([]);
      setSelectedHotel(hotel);

      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 400));
        const rates = RATEHAWK_RATES_BY_HID[hotel.hid] ?? [];
        setRoomGroups(groupRatesByRoom(rates));
        setIsLoadingRates(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("stays-rates-rh", {
          body: {
            id: hotel.id,
            hid: hotel.hid,
            checkin: searchParams.checkin,
            checkout: searchParams.checkout,
            guests: searchParams.guests,
            currency: searchParams.currency ?? "EUR",
            residency: searchParams.residency ?? "it",
          },
        });

        if (error) throw error;

        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        // Edge function returns { data: { hotels: [{ id, hid, rates, metapolicy_struct, metapolicy_extra_info }] } }
        const hotels = parsed?.data?.hotels ?? parsed?.hotels ?? [];
        const firstHotel = hotels[0];
        const rates: RateHawkRate[] = firstHotel?.rates ?? [];
        setRoomGroups(groupRatesByRoom(rates));
        setSelectedHotel((prev) =>
          prev
            ? {
                ...prev,
                metapolicy_struct: firstHotel?.metapolicy_struct ?? null,
                metapolicy_extra_info: firstHotel?.metapolicy_extra_info ?? null,
                // La ricerca porta solo coverPhoto e un estratto: gallery e
                // descrizione complete arrivano da qui.
                photos: firstHotel?.photos?.length ? firstHotel.photos : prev.photos,
                description: firstHotel?.description || prev.description,
              }
            : prev,
        );
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to load rates";
        setRatesError(message);
      } finally {
        setIsLoadingRates(false);
      }
    },
    [],
  );

  const clearSearch = useCallback(() => {
    // Invalida anche le richieste in volo: senza, la risposta di una ricerca
    // avviata prima della chiusura ripopolerebbe una lista appena svuotata.
    searchSeqRef.current++;
    cacheKeyRef.current = null;
    setSearchResults([]);
    setTotalHotels(null);
    setTotalFiltered(null);
    setPage(1);
    setTotalPages(1);
    setFacets({ kinds: [], amenities: [] });
    setSearchMode(null);
    setIsSearching(false);
    setSearchError(null);
    setRoomGroups([]);
    setSelectedHotel(null);
  }, []);

  const clearRates = useCallback(() => {
    setRoomGroups([]);
    setSelectedHotel(null);
    setIsLoadingRates(false);
    setRatesError(null);
  }, []);

  return {
    isMockMode: USE_MOCK,
    searchResults,
    totalHotels,
    totalFiltered,
    page,
    totalPages,
    facets,
    searchMode,
    isSearching,
    searchError,
    searchStays,
    roomGroups,
    selectedHotel,
    isLoadingRates,
    ratesError,
    fetchRates,
    clearSearch,
    clearRates,
  };
}
